/**
 * errorInterpreter.js
 * Centralized utility to parse axios error responses into human-friendly strings,
 * plus a rich PacisNotify system that renders branded toast notifications.
 */

/* ─────────────────────────────────────────────────────────────────────────────
   1. parseError — converts any axios/JS error into a readable string
───────────────────────────────────────────────────────────────────────────── */
export const parseError = (err) => {
  if (!err) return 'An unexpected error occurred. Please try again.';

  // 1. Nested API error objects (e.g. { data: { data: { message } } })
  const nested = err.response?.data?.data;
  if (nested?.message) return nested.message;
  if (typeof nested === 'string' && nested.length < 300) return nested;

  // 2. Standard backend message / error fields
  if (err.response?.data?.message) return err.response.data.message;
  if (err.response?.data?.error) return err.response.data.error;

  // 3. HTTP status fallbacks
  switch (err.response?.status) {
    case 400: return 'Invalid request. Please check your input and try again.';
    case 401: return 'Your session has expired. Please sign in again.';
    case 403: return 'You do not have permission to perform this action.';
    case 404: return 'The requested resource was not found.';
    case 409: return 'A conflict occurred. The resource may already exist.';
    case 413: return 'The file you uploaded is too large.';
    case 422: return 'The data provided could not be processed. Please review and try again.';
    case 429: return 'Too many requests. Please wait a moment before trying again.';
    case 500: return 'Server error. Our team has been notified — please try again shortly.';
    case 502: return 'Service temporarily unavailable. Please try again in a moment.';
    case 503: return 'Service is down for maintenance. Please try again later.';
    case 504: return 'The request timed out on the server. Please try again.';
    default: break;
  }

  // 4. Network / timeout
  if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
    return 'Network error. Please check your internet connection and try again.';
  }
  if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }

  // 5. Absolute fallback
  return err.message || 'An unexpected error occurred. Please try again.';
};

/* ─────────────────────────────────────────────────────────────────────────────
   2. PacisNotify — inject once in main.js, use as this.$notify anywhere

   Install in main.js:
     import { PacisNotify } from '@/utils/errorInterpreter';
     Vue.use(PacisNotify);

   Usage:
     this.$notify.success('Policy saved!', 'Success')
     this.$notify.error('Something went wrong', 'Upload Failed')
     this.$notify.info('Quote expires in 30 days')
     this.$notify.warn('Vehicle older than 12 years')
     this.$notify.fromError(err, 'Upload Failed')   ← most common pattern
───────────────────────────────────────────────────────────────────────────── */

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

  #pacis-notify-root {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 99999;
    display: flex;
    flex-direction: column;
    gap: 10px;
    pointer-events: none;
    max-width: 380px;
    width: calc(100vw - 40px);
    font-family: 'DM Sans', sans-serif;
  }

  .pn-toast {
    pointer-events: all;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 4px 24px rgba(30, 66, 118, 0.13), 0 1px 4px rgba(0,0,0,0.06);
    border-left: 4px solid transparent;
    animation: pn-slide-in 0.32s cubic-bezier(0.34, 1.56, 0.64, 1) both;
    position: relative;
    overflow: hidden;
    cursor: default;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .pn-toast:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 32px rgba(30, 66, 118, 0.16), 0 2px 8px rgba(0,0,0,0.08);
  }

  .pn-toast.pn-leaving {
    animation: pn-slide-out 0.28s ease forwards;
  }

  .pn-toast::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    background: currentColor;
    opacity: 0.18;
    animation: pn-progress linear forwards;
  }

  /* Variants */
  .pn-success { border-left-color: #10B981; }
  .pn-success .pn-icon-wrap { background: #ECFDF5; color: #10B981; }
  .pn-success .pn-title { color: #065F46; }
  .pn-success::after { color: #10B981; }

  .pn-error { border-left-color: #EF4444; }
  .pn-error .pn-icon-wrap { background: #FEF2F2; color: #EF4444; }
  .pn-error .pn-title { color: #991B1B; }
  .pn-error::after { color: #EF4444; }

  .pn-warn { border-left-color: #F59E0B; }
  .pn-warn .pn-icon-wrap { background: #FFFBEB; color: #D97706; }
  .pn-warn .pn-title { color: #92400E; }
  .pn-warn::after { color: #F59E0B; }

  .pn-info { border-left-color: #00AEEF; }
  .pn-info .pn-icon-wrap { background: #EBF8FF; color: #00AEEF; }
  .pn-info .pn-title { color: #1E4276; }
  .pn-info::after { color: #00AEEF; }

  .pn-icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .pn-icon-wrap svg {
    width: 18px;
    height: 18px;
    stroke-width: 2.2;
  }

  .pn-body {
    flex: 1;
    min-width: 0;
  }

  .pn-title {
    font-size: 0.85rem;
    font-weight: 700;
    margin: 0 0 2px;
    line-height: 1.3;
    letter-spacing: -0.01em;
  }

  .pn-message {
    font-size: 0.8rem;
    color: #475569;
    margin: 0;
    line-height: 1.5;
    word-break: break-word;
  }

  .pn-close {
    background: none;
    border: none;
    padding: 2px;
    cursor: pointer;
    color: #94a3b8;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    flex-shrink: 0;
    transition: color 0.15s, background 0.15s;
    margin-top: -1px;
  }

  .pn-close:hover {
    color: #475569;
    background: #f1f5f9;
  }

  .pn-close svg {
    width: 14px;
    height: 14px;
    stroke-width: 2.5;
  }

  .pn-brand-dot {
    position: absolute;
    bottom: 8px;
    right: 14px;
    font-size: 0.6rem;
    font-weight: 700;
    color: #CBD5E1;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  @keyframes pn-slide-in {
    from { opacity: 0; transform: translateX(110%) scale(0.9); }
    to   { opacity: 1; transform: translateX(0)   scale(1);   }
  }

  @keyframes pn-slide-out {
    from { opacity: 1; transform: translateX(0) scale(1);   max-height: 120px; margin-bottom: 0; }
    to   { opacity: 0; transform: translateX(110%) scale(0.9); max-height: 0;   margin-bottom: -10px; }
  }

  @keyframes pn-progress {
    from { width: 100%; }
    to   { width: 0%;   }
  }

  @media (max-width: 480px) {
    #pacis-notify-root {
      top: auto;
      bottom: 80px;
      right: 12px;
      left: 12px;
      max-width: 100%;
      width: auto;
    }
    @keyframes pn-slide-in {
      from { opacity: 0; transform: translateY(40px) scale(0.95); }
      to   { opacity: 1; transform: translateY(0)    scale(1);    }
    }
    @keyframes pn-slide-out {
      from { opacity: 1; transform: translateY(0) scale(1); }
      to   { opacity: 0; transform: translateY(20px) scale(0.95); }
    }
  }
`;

const ICONS = {
  success: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="20 6 9 17 4 12"/></svg>',
  error: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
  warn: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
  info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',
};

const DEFAULT_TITLES = {
  success: 'Success',
  error: 'Something went wrong',
  warn: 'Heads up',
  info: 'Info',
};

const DEFAULT_DURATION = {
  success: 4000,
  error: 6000,
  warn: 5000,
  info: 4500,
};

function ensureRoot() {
  let root = document.getElementById('pacis-notify-root');
  if (!root) {
    // Inject styles once
    if (!document.getElementById('pacis-notify-styles')) {
      const style = document.createElement('style');
      style.id = 'pacis-notify-styles';
      style.textContent = STYLES;
      document.head.appendChild(style);
    }
    root = document.createElement('div');
    root.id = 'pacis-notify-root';
    document.body.appendChild(root);
  }
  return root;
}

function show(type, message, title, duration) {
  const root = ensureRoot();
  const dur = duration ?? DEFAULT_DURATION[type];
  const ttl = title || DEFAULT_TITLES[type];

  const toast = document.createElement('div');
  toast.className = `pn-toast pn-${type}`;
  toast.style.setProperty('--dur', `${dur}ms`);

  toast.innerHTML = `
    <div class="pn-icon-wrap">${ICONS[type]}</div>
    <div class="pn-body">
      <p class="pn-title">${ttl}</p>
      <p class="pn-message">${message}</p>
    </div>
    <button class="pn-close" aria-label="Dismiss">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
    </button>
    <span class="pn-brand-dot">Pacis</span>
  `;

  // Progress bar timing
  const uid = `pn-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  toast.dataset.id = uid;
  const dynStyle = document.createElement('style');
  dynStyle.textContent = `[data-id="${uid}"]::after { animation-duration: ${dur}ms; }`;
  document.head.appendChild(dynStyle);

  const dismiss = () => {
    toast.classList.add('pn-leaving');
    toast.addEventListener('animationend', () => {
      toast.remove();
      dynStyle.remove();
    }, { once: true });
  };

  toast.querySelector('.pn-close').addEventListener('click', dismiss);
  root.appendChild(toast);

  if (dur > 0) setTimeout(dismiss, dur);

  return { dismiss };
}

export const PacisNotify = {
  /* eslint-disable no-param-reassign */
  install(Vue) {
    const notifier = {
      success: (message, title, duration) => show('success', message, title, duration),
      error: (message, title, duration) => show('error', message, title, duration),
      warn: (message, title, duration) => show('warn', message, title, duration),
      info: (message, title, duration) => show('info', message, title, duration),
      /**
             * Convenience: parse an axios error and show an error toast.
             * @param {Error} err      - axios error object
             * @param {string} [title] - optional custom title
             */
      fromError: (err, title) => {
        const msg = parseError(err);
        return show('error', msg, title || DEFAULT_TITLES.error);
      },
    };

    Vue.prototype.$notify = notifier;
    // Also expose globally for use outside components (e.g. Vuex actions)
    Vue.$notify = notifier;
  },
  /* eslint-enable no-param-reassign */
};

export default { parseError, PacisNotify };
